import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import compose from 'lodash.flowright';
import { useSelector } from 'react-redux';

import { getAsset } from '../../../../util/assets';
import Button from '../../../../components/global/Button';
import getLogger from '../../../../util/logger';
import { PHONE_AUTH_MODAL } from '../../../../constants/modals';
import {
  trackAuthEvent,
  trackWebGalleryEvent,
  ANALYTIC_EVENTS,
} from '../../utilities/analytics';
import useOpenModal from '../../../../hooks/useOpenModal';
import withFavoritePhotosMutations from '../../hoc/withFavoritePhotosMutations';
import { allowsPhotoPurchase } from '../../../../util/album';
import { claimFirstTipShow } from '../../utilities/tip-storage';
import { WEB_GALLERY_FAVORITE_DOWNLOAD_TIP_MODAL } from '../WebGalleryFavoriteDownloadTipModal/WebGalleryFavoriteDownloadTipModal';

const log = getLogger('WebGalleryFavoritePhotoButton');

const propTypes = {
  photo: PropTypes.shape({
    id: PropTypes.string.isRequired,
    is_favorite: PropTypes.bool,
  }),
  album: PropTypes.shape({
    id: PropTypes.string,
    root_album_folder: PropTypes.shape({
      id: PropTypes.string,
    }),
  }),
  favoritePhotosCreate: PropTypes.func.isRequired,
  favoritePhotosDelete: PropTypes.func.isRequired,
  children: PropTypes.func,
  // Basically used for backwards compatibility.
  // Opt for render props when possible.
  size: PropTypes.oneOf(['sm']),
  className: PropTypes.string,
  label: PropTypes.bool,
};

const WebGalleryFavoritePhotoButton = ({
  size,
  label,
  photo,
  album,
  children,
  className,
  favoritePhotosCreate,
  favoritePhotosDelete,
}) => {
  const { id: photoId, is_favorite: isFavorite } = photo;
  const [loading, setLoading] = useState(false);
  const authenticated = useSelector(({ auth }) => auth.authenticated);
  const openModal = useOpenModal();

  const onFavoriteToggle = async () => {
    if (!authenticated) {
      openModal(PHONE_AUTH_MODAL, {
        onLogin: trackAuthEvent,
        introText: (
          <Fragment>
            Enter your mobile number to create and save your favorite photos!
          </Fragment>
        ),
        submitText: 'Send PIN Code',
      });

      return;
    }

    if (loading) {
      return;
    }

    setLoading(true);
    try {
      if (isFavorite) {
        await favoritePhotosDelete(photoId);
        trackWebGalleryEvent(
          ANALYTIC_EVENTS.PHOTO_BROWSER_DID_UNFAVORITE_PHOTO,
          {
            photo_id: photoId,
          },
        );
      } else {
        await favoritePhotosCreate(photoId);
        trackWebGalleryEvent(ANALYTIC_EVENTS.PHOTO_BROWSER_DID_FAVORITE_PHOTO, {
          photo_id: photoId,
        });

        // On the first favorite in a gallery that prices hi-res downloads
        // surface a one-time tip letting the user know to save as they go.
        const galleryId = album?.root_album_folder?.id ?? album?.id;

        if (
          galleryId &&
          allowsPhotoPurchase(album) &&
          claimFirstTipShow(`HAS_SEEN_FAVORITE_DOWNLOAD_TIP_FOR_${galleryId}`)
        ) {
          openModal(WEB_GALLERY_FAVORITE_DOWNLOAD_TIP_MODAL);
        }
      }
    } catch (error) {
      log.error(
        'onFavoriteToggle() :: Error in photo favorites',
        error,
        photoId,
      );
    } finally {
      setLoading(false);
    }
  };

  const altText = isFavorite ? 'Remove from favorites' : 'Add to favorites';
  const icon = isFavorite
    ? '/icon-heart-filled.svg'
    : '/icon-heart-outline.svg';

  if (typeof children === 'function') {
    return children({ onFavoriteToggle, photo });
  }

  return (
    <Button
      btnStyle="reset"
      onClick={onFavoriteToggle}
      className={className}
      disabled={loading}
      title={altText}
    >
      <img
        src={getAsset(icon)}
        alt={altText}
        width={size === 'sm' ? 20 : undefined}
        height={size === 'sm' ? 20 : undefined}
        className={label && 'mr-1'}
      />
      {label && <span className="d-none d-md-inline-block">Favorite</span>}
    </Button>
  );
};

WebGalleryFavoritePhotoButton.propTypes = propTypes;

export { WebGalleryFavoritePhotoButton as WebGalleryFavoritePhotoButtonComponent };

export default compose(withFavoritePhotosMutations)(
  WebGalleryFavoritePhotoButton,
);
